import React, { useState, useEffect } from 'react'
import { Modal, Form } from 'react-bootstrap'
import styles from './CallerModal.module.css'
import Button from '@components/common/Button/Button.component'

type CallerModalProps = {
  show: boolean
  onHide: () => void
  phoneNumber: string
  backdropClassName?: string
  customClassName?: string
}

const CallerModal: React.FC<CallerModalProps> = ({
  show,
  onHide,
  phoneNumber,
  backdropClassName,
  customClassName,
}) => {
  const [dialNumber, setDialNumber] = useState(phoneNumber)
  const [isCalling, setIsCalling] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState('US')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    setDialNumber(phoneNumber)
  }, [phoneNumber])

  const validatePhoneNumber = (number: string): boolean => {
    const phoneNumberRegex = /^[+]*[0-9]{10,15}$/
    return phoneNumberRegex.test(number)
  }

  const handleCallClick = (): void => {
    if (validatePhoneNumber(dialNumber)) {
      setErrorMessage('')
      setIsCalling(true)
      setTimeout(() => {
        setIsCalling(false)
        onHide()
      }, 20000)
    } else {
      setErrorMessage('Please enter a valid phone number!')
    }
  }

  const handleDialClick = (digit: string): void => {
    setDialNumber((prev) => prev + digit)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace') {
      setDialNumber((prev) => prev.slice(0, -1))
    } else if (!isNaN(Number(e.key))) {
      setDialNumber((prev) => prev + e.key)
    }
  }

  useEffect(() => {
    if (show) {
      handleCallClick()
    }
  }, [show])

  const dialPad = [
    { number: '1', letters: '' },
    { number: '2', letters: 'ABC' },
    { number: '3', letters: 'DEF' },
    { number: '4', letters: 'GHI' },
    { number: '5', letters: 'JKL' },
    { number: '6', letters: 'MNO' },
    { number: '7', letters: 'PQRS' },
    { number: '8', letters: 'TUV' },
    { number: '9', letters: 'WXYZ' },
    { number: '*', letters: '' },
    { number: '0', letters: '+' },
    { number: '#', letters: '' },
  ]

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName={styles['caller-modal']}
      className={customClassName}
      backdropClassName={backdropClassName}
      centered
    >
      <Modal.Header closeButton style={{ backgroundColor: '#EFF1F3' }}>
        <Modal.Title>VoIP Caller</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles['input-container']}>
          <div className={styles['country-select-container']}>
            <Form.Select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className={styles['country-select']}
            >
              <option value="US">US</option>
              <option value="UK">UK</option>
              <option value="CA">CA</option>
            </Form.Select>
          </div>
          <Form.Control
            type="text"
            value={dialNumber}
            readOnly
            className={styles['phone-input']}
            onKeyDown={handleKeyDown}
          />
        </div>

        {errorMessage && (
          <div className={styles['error-message']}>{errorMessage}</div>
        )}

        <div className={styles['dial-pad']}>
          {dialPad.map((key) => (
            <div
              key={key.number}
              className={styles['dial-button']}
              onClick={() => handleDialClick(key.number)}
            >
              <span className={styles['digit']}>{key.number}</span>
              {key.letters && (
                <span className={styles['letters']}>{key.letters}</span>
              )}
            </div>
          ))}
        </div>
        <div className="dial-button">
          <Button
            size="md"
            disabled={isCalling}
            className={styles['dial-call-button']}
            onClick={handleCallClick}
          >
            {isCalling ? 'Calling...' : 'Call'}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default CallerModal
